import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { tenantPlanService } from "../services/tenantPlanService";
import {
  TenantPlanValuesInit,
  getTenantPlanCreateVariables,
  getTenantPlanValues,
  TenantPlanValuesType,
  TenantPlanErrorsType,
  tenantPlanDisplayId,
} from "./TenantPlanCommonStore";
import { TenantPlan } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class TenantPlanCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: TenantPlanErrorsType = {};
  service = tenantPlanService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: TenantPlan | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = TenantPlanValuesInit();
  name_esError: string | undefined;
  name_enError: string | undefined;
  name_ptError: string | undefined;
  description_esError: string | undefined;
  description_enError: string | undefined;
  description_ptError: string | undefined;
  limitsError: string | undefined;

  private customValidators: CustomValidator<TenantPlanValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<TenantPlanValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<TenantPlanValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return tenantPlanDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantPlanSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupTenantPlanLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  getName_esValue(): string {
    return this.entityValues.name_es;
  }

  setName_esValue(value: string) {
    this.entityValues.name_es = value;
  }

  getName_enValue(): string {
    return this.entityValues.name_en;
  }

  setName_enValue(value: string) {
    this.entityValues.name_en = value;
  }

  getName_ptValue(): string {
    return this.entityValues.name_pt;
  }

  setName_ptValue(value: string) {
    this.entityValues.name_pt = value;
  }

  getDescription_esValue(): string {
    return this.entityValues.description_es;
  }

  setDescription_esValue(value: string) {
    this.entityValues.description_es = value;
  }

  getDescription_enValue(): string {
    return this.entityValues.description_en;
  }

  setDescription_enValue(value: string) {
    this.entityValues.description_en = value;
  }

  getDescription_ptValue(): string {
    return this.entityValues.description_pt;
  }

  setDescription_ptValue(value: string) {
    this.entityValues.description_pt = value;
  }

  getLimitsValue(): string {
    return this.entityValues.limits;
  }

  setLimitsValue(value: string) {
    this.entityValues.limits = value;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = TenantPlanValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: TenantPlanValuesType): TenantPlanErrorsType {
    const errors: TenantPlanErrorsType = {};
    this.clearErrors();
    values.name_es = values.name_es.trim();
    if (values.name_es === "") {
      errors.name_es = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }
    values.name_en = values.name_en.trim();
    if (values.name_en === "") {
      errors.name_en = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }
    values.name_pt = values.name_pt.trim();
    if (values.name_pt === "") {
      errors.name_pt = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }
    values.name_es = values.name_es.trim();
    if (values.name_es === "") {
      errors.name_es = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }
    values.name_en = values.name_en.trim();
    if (values.name_en === "") {
      errors.name_en = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }
    values.name_pt = values.name_pt.trim();
    if (values.name_pt === "") {
      errors.name_pt = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }
    values.name_es = values.name_es.trim();
    if (values.name_es === "") {
      errors.name_es = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }
    values.name_en = values.name_en.trim();
    if (values.name_en === "") {
      errors.name_en = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }
    values.name_pt = values.name_pt.trim();
    if (values.name_pt === "") {
      errors.name_pt = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.TenantPlanNameLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "TenantPlanCreateStore"
    );
  }

  *duplicatesTenantPlan(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const tenantPlanInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (tenantPlanInstance) {
      this.entityValues = getTenantPlanValues(
        tenantPlanInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(
        `/tenantPlan/update/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewTenantPlan(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewTenantPlan(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name_es: this.entityValues.name_es,
      name_en: this.entityValues.name_en,
      name_pt: this.entityValues.name_pt,
      description_es: this.entityValues.description_es,
      description_en: this.entityValues.description_en,
      description_pt: this.entityValues.description_pt,
      limits: this.entityValues.limits,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getTenantPlanValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = TenantPlanValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getTenantPlanCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantPlanCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.tenantPlanCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.tenantPlanCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesTenantPlan(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        (this.entityValues as any)[this.langStore.fieldByLang("name")] =
          initialDisplayId;
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
